import { useEffect, useId, useRef, useState } from "react";

import { CommandFeedback } from "components/CommandFeedback";
import { Button } from "components/ui/button";

import { eventDays, staleDayTitles } from "../day-titles";
import {
  dayLabel,
  type DayRange,
  type OutsideDates,
  outsideDatesMessage,
  rangeLabel,
  setDatesLabel,
  shortensEvent,
  tooLongMessage,
} from "../event-dates-fit";
import type { EventSettings } from "../schemas";
import { useEventSettingsWrite } from "../use-event-settings-writes";

import { AfterEventNotificationTimeCheck } from "./AfterEventSettings";
import { WarningNote } from "./WarningNote";

/**
 * 44px tall and the width of the box on a phone, where the label wraps onto a
 * second line instead of running past the edge at 320px.
 */
const WRAPPING_BUTTON =
  "h-11 max-sm:h-auto max-sm:min-h-11 max-sm:w-full max-sm:whitespace-normal max-sm:py-2 sm:h-10";

type Step = "idle" | "confirming" | "done";

/**
 * Sessions on days the event does not have, and the one button that fixes it.
 *
 * Shared by the Agenda tab (sessions already saved) and the CSV import panel
 * (rows about to be). The dates are written through the same event settings
 * write the Settings tab uses, `startDate` and `endDate` only, so the rules
 * check them as they check any other change of dates. The button asks once,
 * in line, before it writes.
 *
 * Keyboard and screen readers. The note itself is not a live region, because
 * it holds buttons and changes in place, and a live region re-reads all of
 * that on every change. Focus does the moving instead: opening the question
 * puts focus on it, Cancel and a refused write put it back on the button, and
 * a saved write puts it on the result. The short texts that matter (saving,
 * saved, refused) go through one always-mounted polite region, as in the day
 * titles editor, since a region that appears with its text is often not read.
 *
 * It stays mounted after the write: the event is read again and the warning
 * goes away with it, so the result has to be said by something that is still
 * there. Day titles are not touched here. Titles for days the new dates no
 * longer include are dropped when the day titles are next saved, and the note
 * says so.
 */
export function OutsideDatesNote({
  eventId,
  uid,
  timeZone,
  event,
  reload,
  info,
  subject,
  testId,
}: {
  eventId: string;
  uid: string | undefined;
  /** The event's zone when this browser can resolve it. */
  timeZone: string | undefined;
  event: Pick<EventSettings, "startDate" | "endDate" | "dayTitles">;
  /** Reads the event again once its dates are written. */
  reload: () => void;
  /** What is outside, or null when nothing is. */
  info: OutsideDates | null;
  subject: "agenda" | "file";
  testId: string;
}) {
  const { save, saving, error, clearError } = useEventSettingsWrite(eventId);
  const [step, setStep] = useState<Step>("idle");
  const [result, setResult] = useState<{
    range: DayRange;
    withYear: boolean;
    endMoved: boolean;
  } | null>(null);

  const questionId = useId();
  const confirmingRef = useRef<HTMLSpanElement>(null);
  const setRef = useRef<HTMLButtonElement>(null);
  const resultRef = useRef<HTMLDivElement>(null);
  // Set when focus should go back to the "Set the event dates" button once it
  // is on the page again.
  const focusSetButton = useRef(false);

  useEffect(() => {
    if (step === "confirming") confirmingRef.current?.focus();
    else if (step === "done") resultRef.current?.focus();
    else if (focusSetButton.current) {
      focusSetButton.current = false;
      setRef.current?.focus();
    }
  }, [step]);

  async function apply(range: DayRange, withYear: boolean) {
    const ok = await save({ startDate: range.first, endDate: range.last });
    if (!ok) {
      focusSetButton.current = true;
      setStep("idle");
      return;
    }
    setResult({ range, withYear, endMoved: range.last !== event.endDate });
    setStep("done");
    reload();
  }

  const successText = result
    ? `The event ${
        result.range.first === result.range.last ? "date is" : "dates are"
      } now ${rangeLabel(result.range, result.withYear)}.`
    : "";
  const announcement = saving
    ? "Saving the event dates…"
    : step === "done"
      ? successText
      : error
        ? `The event dates were not saved. ${error}`
        : "";

  const live = (
    <p role="status" className="sr-only" data-testid={`${testId}-live`}>
      {announcement}
    </p>
  );

  if (step === "done" && result) {
    return (
      <>
        <div
          ref={resultRef}
          tabIndex={-1}
          className="space-y-2 focus:outline-hidden"
          data-testid={testId}
        >
          <CommandFeedback
            state={{ status: "succeeded" }}
            onReset={() => {
              setStep("idle");
              setResult(null);
            }}
            pendingMessage=""
            successMessage={successText}
            testId={`${testId}-result`}
          />
          {/* A booked "summary is ready" notification keeps the send time of
              the old end, as after a save on the Settings tab. */}
          {result.endMoved && uid && timeZone && (
            <AfterEventNotificationTimeCheck
              key={`${result.range.last}|${timeZone}`}
              eventId={eventId}
              uid={uid}
              endDate={result.range.last}
              timeZone={timeZone}
            />
          )}
        </div>
        {live}
      </>
    );
  }

  if (!info) return live;

  const { offer, withYear } = info;
  const droppedTitles =
    offer.kind === "set" &&
    staleDayTitles(
      eventDays(offer.range.first, offer.range.last).days,
      event.dayTitles,
    ).length > 0;
  const shortens =
    offer.kind === "set" && shortensEvent(info.event, offer.range);

  return (
    <>
      <div className="space-y-2" data-testid={testId}>
        <WarningNote role="none" testId={`${testId}-warning`}>
          <span className="block">{outsideDatesMessage(info, subject)}</span>

          {offer.kind === "too-long" ? (
            <span className="mt-1 block" data-testid={`${testId}-too-long`}>
              {tooLongMessage(offer, withYear)}
            </span>
          ) : (
            <>
              {droppedTitles && (
                <span className="mt-1 block" data-testid={`${testId}-titles`}>
                  Day titles for days that are no longer part of the event are
                  dropped the next time you save the day titles.
                </span>
              )}

              {step === "confirming" ? (
                <span
                  ref={confirmingRef}
                  tabIndex={-1}
                  role="group"
                  aria-labelledby={questionId}
                  className="mt-2 block space-y-2 focus:outline-hidden"
                  data-testid={`${testId}-confirming`}
                >
                  <span
                    id={questionId}
                    className="block font-medium"
                    data-testid={`${testId}-question`}
                  >
                    Replace {rangeLabel(info.event, withYear)} with{" "}
                    {rangeLabel(offer.range, withYear)} as the event{" "}
                    {offer.range.first === offer.range.last ? "date" : "dates"}?
                  </span>
                  {shortens && (
                    <span className="block" data-testid={`${testId}-shortens`}>
                      This shortens the event: reminders and the start
                      notification follow the new start date.
                    </span>
                  )}
                  <span className="flex flex-col gap-2 sm:flex-row">
                    <Button
                      variant="secondary"
                      className={WRAPPING_BUTTON}
                      data-testid={`${testId}-confirm`}
                      disabled={saving}
                      onClick={() => void apply(offer.range, withYear)}
                    >
                      {saving
                        ? "Saving…"
                        : `Yes, use ${dayLabel(offer.range.first, withYear)}${
                            offer.range.first === offer.range.last
                              ? ""
                              : ` to ${dayLabel(offer.range.last, withYear)}`
                          }`}
                    </Button>
                    <Button
                      variant="outline"
                      className={WRAPPING_BUTTON}
                      data-testid={`${testId}-cancel`}
                      disabled={saving}
                      onClick={() => {
                        focusSetButton.current = true;
                        setStep("idle");
                      }}
                    >
                      Cancel
                    </Button>
                  </span>
                </span>
              ) : (
                <span className="mt-2 block">
                  <Button
                    ref={setRef}
                    variant="outline"
                    className={WRAPPING_BUTTON}
                    data-testid={`${testId}-set`}
                    onClick={() => {
                      clearError();
                      setStep("confirming");
                    }}
                  >
                    {setDatesLabel(offer.range, withYear)}
                  </Button>
                </span>
              )}
            </>
          )}
        </WarningNote>

        {saving && (
          <CommandFeedback
            state={{ status: "pending" }}
            onReset={() => undefined}
            pendingMessage="Saving the event dates…"
            successMessage=""
            testId={`${testId}-result`}
          />
        )}
        {error && !saving && (
          <CommandFeedback
            state={{ status: "failed", error }}
            onReset={clearError}
            pendingMessage=""
            successMessage=""
            testId={`${testId}-result`}
          />
        )}
      </div>
      {live}
    </>
  );
}
